import { url } from "@mozioinc/utils";
import { useTranslation } from "react-i18next";
import {
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaTwitter,
} from "react-icons/fa";
import { getUrlByLocale, URL_NAMES } from "../utils/translation";
import { getWhiteLabel } from "../utils/white-label";
import { Container } from "./container";
import { Link } from "./link";
import type { TransNamespace } from "../types";
import type { ReactNode } from "react";

type Image = {
  src: string;
  srcSet?: string;
  alt: string;
};

export type FooterProps = {
  phoneNumber: string;
  emailLink: string;
  logo?: Image & { href: string };
  socials?: { icon: ReactNode; href: string }[];
  navLinks?: { href: string; content: ReactNode }[];
  copyRight?: ReactNode;
  termsAndPrivacyLinks?: {
    href: string;
    content: string;
  }[];
};

export const Footer = () => {
  const { footer } = getWhiteLabel();
  const { t } = useTranslation<TransNamespace>("esim:footer");
  const { i18n } = useTranslation();

  const { phoneNumber, emailLink, logo } = footer;

  const socials = footer.socials
    ? [
        footer.socials.facebook && {
          href: footer.socials.facebook,
          icon: <FaFacebookF />,
        },
        footer.socials.instagram && {
          href: footer.socials.instagram,
          icon: <FaInstagram />,
        },
        footer.socials.linkedin && {
          href: footer.socials.linkedin,
          icon: <FaLinkedinIn />,
        },
        footer.socials.twitter && {
          href: footer.socials.twitter,
          icon: <FaTwitter />,
        },
      ].filter(Boolean)
    : [
        {
          href: "https://www.facebook.com/letsmozio/",
          icon: <FaFacebookF />,
        },
        {
          href: "https://twitter.com/letsmozio",
          icon: <FaInstagram />,
        },
        {
          href: "https://www.instagram.com/letsmozio/",
          icon: <FaLinkedinIn />,
        },
        {
          href: "https://www.linkedin.com/company/mozio/",
          icon: <FaTwitter />,
        },
      ];

  const navLinks = [
    {
      href: "https://www.mozio.com/about",
      content: t("About Us"),
    },
    {
      href: getUrlByLocale(URL_NAMES.FAQ, i18n.language),
      content: t("FAQ"),
    },
    {
      href: "https://www.moziogroup.com/",
      content: t("Partners"),
    },
    {
      href: "https://www.mozio.com/provider",
      content: t("Providers"),
    },
    {
      href: "https://www.mozio.com/guide/resort",
      content: t("Resorts"),
    },
    {
      href: "https://www.mozio.com/guide/airport",
      content: t("Airports"),
    },
  ];

  const termsAndPrivacyLinks = footer.termsAndPrivacyLinks || [
    {
      href: "https://www.mozio.com/privacy-policy",
      content: t("Privacy Policy"),
    },
    {
      href: url.setPathname("/esim/terms-of-service"),
      content: t("Terms Of Services"),
    },
    {
      href: "https://www.mozio.com/en-us/imprint",
      content: t("Imprint"),
    },
  ];

  const renderLogoAndSocials = () => (
    <div className="flex items-center justify-center gap-3 text-white md:justify-start">
      <Link color="inherit" href="/">
        <img className="h-9" src={logo} alt={t("Logo")} />
      </Link>
      {socials.map((social) => (
        <Link
          className="grid h-5 w-5 place-items-center rounded-full bg-gray-500 [&_svg]:h-3 [&_svg]:w-3"
          color="inherit"
          key={social.href}
          href={social.href}
          target="_blank"
        >
          {social.icon}
        </Link>
      ))}
    </div>
  );

  const renderNavLinks = () => (
    <div className="flex flex-wrap justify-center gap-x-5 md:justify-end">
      {navLinks.map((link) => (
        <Link color={"primary"} key={link.href} href={link.href}>
          {link.content}
        </Link>
      ))}
    </div>
  );

  const renderTermsAndPolicyLinks = () => (
    <div className="flex justify-center gap-x-4 text-xs">
      {termsAndPrivacyLinks.map(({ content, href }) => (
        <Link key={href} href={href} target="_blank">
          {content}
        </Link>
      ))}
    </div>
  );

  const renderPhoneAndEmail = () => (
    <div className="flex items-center justify-center gap-4">
      {phoneNumber[i18n.language] ?? phoneNumber.default}
      <Link
        href={emailLink.includes("@") ? `mailto: ${emailLink}` : emailLink}
        target="_blank"
      >
        <FaEnvelope />
      </Link>
    </div>
  );

  const renderCopyRight = () => (
    <div className="text-xs">
      {t(
        "© {{year}} Mozio Inc. Trademarks and brands are property of their respective owners.",
        { year: new Date().getFullYear() },
      )}
    </div>
  );

  return (
    <footer className="py-14 text-sm text-gray-500">
      <Container className="grid gap-4 text-center md:hidden">
        {renderLogoAndSocials()}
        {renderNavLinks()}

        {renderPhoneAndEmail()}
        {renderTermsAndPolicyLinks()}
        {renderCopyRight()}
      </Container>

      <Container className="hidden gap-4 md:grid">
        <div className="grid grid-cols-[auto_1fr] gap-8">
          <div className="grid">
            {renderLogoAndSocials()}
            {renderTermsAndPolicyLinks()}
          </div>
          {renderNavLinks()}
        </div>

        <div className="flex items-end justify-between">
          {renderCopyRight()}
          {renderPhoneAndEmail()}
        </div>
      </Container>
    </footer>
  );
};
